import { along, pointToLineDistance } from '@turf/turf';
import * as FileSaver from 'file-saver';
import JSZip from 'jszip';
import _ from 'lodash';
import { Button } from 'reactstrap';
import {
  DISTANCE_UNITS,
  LONG_AREA_UNITS,
  ROW_UNITS,
} from '../../../constants/units';
import { useUnitsMap } from '../../../contexts/UnitContext';
import { toCsv } from '../../../utils/csv';
import { useFarmName, useStartYear } from '../../../utils/hooks';
import { getSectionMeta } from '../../../utils/meta';
import { sectionHasGeodata } from '../../../utils/sections';
import { exportFileName } from '../../../utils/utils';
import { headlandSectionTypes } from '../../../constants/fields';

const lngLatColNames = ['longitude', 'latitude'];
const POINT_NAME_DELIMITER = '.';

const UNITS = {
  LONG_AREA_UNITS,
  DISTANCE_UNITS,
  ROW_UNITS,
};

/*
For points generated before row indices were added to the properties, get
the row index based on the row closest to the point. There should be exactly one row
with distance approximately 0 because the points and rows are generated together.
*/
const getRowIndexForPoint = (point, rows) => {
  if (point.properties.row != null) return point.properties.row;
  const distances = rows.features.map((row) => pointToLineDistance(point, row));
  return distances.indexOf(_.min(distances));
};

const getPointColumns = (order) => [
  {
    displayString: 'name',
    accessor: 'pointName',
  },
  {
    displayString: 'section',
    accessor: 'sectionName',
  },
  {
    displayString: 'row',
    accessor: 'rowNumber',
  },
  {
    displayString: 'position',
    accessor: 'position',
  },
  {
    displayString: 'index',
    accessor: 'index',
  },
  {
    displayString: 'crop',
    accessor: 'cropName',
  },
  {
    displayString: lngLatColNames[order[0]],
    accessor: lngLatColNames[order[0]],
  },
  {
    displayString: lngLatColNames[order[1]],
    accessor: lngLatColNames[order[1]],
  },
  {
    displayString: 'elevation',
    accessor: 'elevation',
  },
];

/**
 * Converts the sections' points to CSV with the following fields:
 * - name (`{field}-{index}`)
 * - section
 * - row
 * - position within row
 * - index within field
 * - crop
 * - longitude
 * - latitude
 * - elevation
 *
 * Note: this sets `row` and `cropName` based on the closest row and section crop if
 * they are missing from the point properties.
 */
const sectionsToPointsCsv = (sections, order) => {
  const data = sections
    .filter((section) => sectionHasGeodata(section))
    .flatMap((section) => {
      let position = 1; // This tracks position within each row (1-indexed)
      let prevRowNumber = null;

      const { points, crop, rows, name: sectionName } = section;
      const sectionData = points.features.map((point, i) => {
        const {
          properties: { row, cropName = crop?.name },
          geometry: { coordinates },
        } = point;

        // Check if row is null or undefined for edge case when row number is zero
        // Rows are named as row index + 1 as convention.
        const rowNumber =
          (row == null ? getRowIndexForPoint(point, rows) : row) + 1;

        if (rowNumber !== prevRowNumber) {
          // new row, reset position
          position = 1;
          prevRowNumber = rowNumber;
        } else {
          position += 1;
        }

        const pointName = [sectionName, rowNumber, position].join(
          POINT_NAME_DELIMITER,
        );

        return {
          pointName,
          sectionName,
          rowNumber,
          position,
          index: i + 1,
          cropName,
          latitude: coordinates[1],
          longitude: coordinates[0],
          // Note: Elevation is always set to 0.
          elevation: 0,
        };
      });

      return sectionData;
    });

  if (data.length === 0) return '';

  return toCsv(data, getPointColumns(order));
};

const sectionsToZippedPointsCsv = (sections, order, farmStartYear) => {
  const zip = new JSZip();
  const filenameCounts = {}; // A count for each filename to deduplicate.
  sections.forEach((section) => {
    const fileId = exportFileName(
      `${section.name}-${section.startYear || farmStartYear}`,
    );
    const fileNumber = filenameCounts[fileId] || 1;
    const filename = `${fileId}${fileNumber > 1 ? ` (${fileNumber})` : ''}`;
    filenameCounts[fileId] = fileNumber + 1;
    const csv = sectionsToPointsCsv([section], order);
    if (csv) zip.file(`${filename}.csv`, csv);
  });
  return zip.generateAsync({ type: 'blob' });
};

const getSectionColumns = (unitMap) => [
  { displayString: 'Farm', accessor: 'farmName' },
  { displayString: 'Field', accessor: 'sectionName' },
  { displayString: 'Start Year', accessor: 'startYear' },
  { displayString: 'Primary Crop', accessor: 'cropName' },
  {
    displayString: `Btwn Row Spacing (${unitMap.DISTANCE_UNITS.displayUnit})`,
    accessor: 'betweenRowSpacing',
    format: unitMap.DISTANCE_UNITS.transformToUnit,
  },
  {
    displayString: `In Row Spacing (${unitMap.DISTANCE_UNITS.displayUnit})`,
    accessor: 'inRowSpacing',
    format: unitMap.DISTANCE_UNITS.transformToUnit,
  },
  {
    displayString: `Total ${_.startCase(unitMap.ROW_UNITS.displayUnit)}`,
    accessor: 'totalRowLength',
    format: unitMap.ROW_UNITS.transformToUnit,
  },
  {
    displayString: `Field area (${unitMap.LONG_AREA_UNITS.displayUnit})`,
    accessor: 'acreage',
    format: unitMap.LONG_AREA_UNITS.transformToUnit,
  },
  {
    displayString: `Planted area (${unitMap.LONG_AREA_UNITS.displayUnit})`,
    accessor: 'plantedAcreage',
    format: unitMap.LONG_AREA_UNITS.transformToUnit,
  },
  { displayString: 'Tree Count', accessor: 'treeCount' },
  { displayString: 'Starting Tree Lat', accessor: 'lat' },
  { displayString: 'Starting Tree Long', accessor: 'lng' },
  {
    displayString: `Headland width (${unitMap.DISTANCE_UNITS.displayUnit})`,
    accessor: 'headlandWidth',
    format: unitMap.DISTANCE_UNITS.transformToUnit,
  },
  { displayString: 'Rotation', accessor: 'rotation' },
];

const sectionsToSectionsCsv = (
  sections,
  { unitMap, farmName, farmStartYear },
) => {
  const data = sections.map((section) => {
    const {
      name,
      type,
      betweenRowSpacing,
      inRowSpacing,
      headlandWidth,
      startYear,
      points,
      rotation,
      crop,
    } = section;
    const {
      totalRowLength,
      acreage,
      plantedAcreage,
      treeCount,
    } = getSectionMeta(section);
    return {
      farmName,
      sectionName: name,
      startYear: startYear || farmStartYear,
      cropName: crop?.name || 'Unknown',
      betweenRowSpacing,
      inRowSpacing,
      totalRowLength,
      acreage,
      plantedAcreage,
      treeCount,
      lat: points?.features[0]?.geometry?.coordinates[1],
      lng: points?.features[0]?.geometry?.coordinates[0],
      rotation: type === 'grid' ? rotation : '',
      headlandWidth: headlandSectionTypes.includes(type)
        ? headlandWidth
        : undefined,
    };
  });
  return toCsv(data, getSectionColumns(unitMap));
};

const getRowColumns = (unitMap) => [
  { displayString: 'Farm', accessor: 'farmName' },
  { displayString: 'Field', accessor: 'sectionName' },
  { displayString: 'Row', accessor: 'rowName' },
  {
    displayString: `Btwn Row Spacing (${unitMap.DISTANCE_UNITS.displayUnit})`,
    accessor: 'betweenRowSpacing',
    format: unitMap.DISTANCE_UNITS.transformToUnit,
  },
  {
    displayString: `In Row Spacing (${unitMap.DISTANCE_UNITS.displayUnit})`,
    accessor: 'inRowSpacing',
    format: unitMap.DISTANCE_UNITS.transformToUnit,
  },
  { displayString: 'Crop', accessor: 'cropName' },
  { displayString: 'Tree Count', accessor: 'treeCount' },
  {
    displayString: `Total ${_.startCase(unitMap.ROW_UNITS.displayUnit)}`,
    accessor: 'rowLength',
    format: unitMap.ROW_UNITS.transformToUnit,
  },
  { displayString: 'Starting Tree Lat', accessor: 'lat' },
  { displayString: 'Starting Tree Long', accessor: 'lng' },
  { displayString: 'Rotation', accessor: 'rotation' },
];

const sectionsToRowsCsv = (sections, { unitMap, farmName }) => {
  const data = sections.flatMap((section) => {
    const {
      name,
      type,
      betweenRowSpacing,
      inRowSpacing,
      crop,
      rows,
      points,
      rotation,
      rowOverrides,
    } = section;
    if (!sectionHasGeodata(section)) return [];
    return rows.features.map((row, i) => {
      const { treeCount, rowLength } = row.properties;
      const rowOverride = rowOverrides[i] || {};
      const {
        geometry: { coordinates },
      } =
        points.features.find(
          // Find the first tree point that matches the row.
          (point) => getRowIndexForPoint(point, rows) === i,
          // If there are no points in the row (eg. because the crop is none),
          // use the first point along the row so it is locatable.
        ) || along(row, 0);
      return {
        farmName,
        sectionName: name,
        rowName: i + 1,
        betweenRowSpacing,
        inRowSpacing: rowOverride.inRowSpacing || inRowSpacing,
        cropName: rowOverride.cropName || crop?.name || 'Unknown',
        treeCount,
        rowLength,

        lat: coordinates[1],
        lng: coordinates[0],
        rotation: type === 'grid' ? rotation : '',
      };
    });
  });
  return toCsv(data, getRowColumns(unitMap));
};

const ExportCsvButton = ({
  pointOrder,
  sections,
  title,
  granularity,
  shouldUseSingleCSV,
  ...buttonProps
}) => {
  const farmName = useFarmName();
  const farmStartYear = useStartYear();
  const unitMap = useUnitsMap(UNITS);

  // Export points as a zip file if there are multiple sections and user didn't specify "single CSV".
  const exportZippedPoints =
    sections.length > 1 && granularity === 'points' && !shouldUseSingleCSV;

  const onClick = async () => {
    // Export ZIP:
    if (exportZippedPoints) {
      const zipContent = await sectionsToZippedPointsCsv(
        sections,
        pointOrder.order,
        farmStartYear,
      );
      // windows limits full file paths to 260 characters
      // truncate to 200 characters to leave some room for the rest of the path
      FileSaver.saveAs(zipContent, exportFileName(title, 'zip'));
      return;
    }

    // Export CSV:
    let csvData;
    switch (granularity) {
      case 'points':
        csvData = sectionsToPointsCsv(sections, pointOrder.order);
        break;
      case 'sections':
        csvData = sectionsToSectionsCsv(sections, {
          unitMap,
          farmName,
          farmStartYear,
        });
        break;
      case 'rows':
        csvData = sectionsToRowsCsv(sections, { unitMap, farmName });
        break;
      default:
        throw new Error(`Invalid granularity: ${granularity}`);
    }

    const csv = new Blob([csvData], { type: 'text/csv' });
    FileSaver.saveAs(csv, exportFileName(title, 'csv'));
  };

  return <Button onClick={onClick} {...buttonProps} />;
};

export default ExportCsvButton;

export const __EXPORTS_FOR_TESTS__ = {
  sectionsToPointsCsv,
  sectionsToSectionsCsv,
  sectionsToRowsCsv,
};
